import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  Row,
  Col,
  DatePicker,
  Input,
  Button,
  App,
} from 'antd';
import dayjs from 'dayjs';
import api from '../../../services/api';

const { TextArea } = Input;
const { Option } = Select;

interface FollowUpModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  leadId: string;
  followUps: any[];
  followUpActions: any[];
  followUpStatuses: any[];
  followUpCategories: any[];
}

export const FollowUpModal: React.FC<FollowUpModalProps> = ({
  open,
  onCancel,
  onSuccess,
  leadId,
  followUps,
  followUpActions,
  followUpStatuses,
  followUpCategories,
}) => {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);

  const [isRaVisitDone, setIsRaVisitDone] = useState(false);
  const [isRiVisitDisabled, setIsRiVisitDisabled] = useState(true);
  const [isRaDisabledByCold, setIsRaDisabledByCold] = useState(false);
  const [showAlasanInput, setShowAlasanInput] = useState(false);
  const [showOtherReasonInput, setShowOtherReasonInput] = useState(false);

  const [reasonInterest, setReasonInterest] = useState<any[]>([]);
  const [reasonInterestLoading, setReasonInterestLoading] = useState(false);
  const [alasanLabel, setAlasanLabel] = useState('Alasan');

  useEffect(() => {
    if (open) {
      form.resetFields();
      setShowAlasanInput(false);
      setShowOtherReasonInput(false);
      setReasonInterest([]);
      setAlasanLabel('Alasan');

      const raVisitDone = followUps.some((fu: any) => fu.schedule_date !== null);
      const riVisitDone = followUps.some((fu: any) => fu.actual_date !== null);

      setIsRaVisitDone(raVisitDone);
      setIsRiVisitDisabled(!raVisitDone || riVisitDone);

      const formValues: { schedule_date?: dayjs.Dayjs; actual_date?: dayjs.Dayjs } = {};

      const existingRaVisit = followUps.find((fu: any) => fu.schedule_date !== null);
      if (existingRaVisit) {
        formValues.schedule_date = dayjs(existingRaVisit.schedule_date);
      }

      const existingRiVisit = followUps.find((fu: any) => fu.actual_date !== null);
      if (existingRiVisit) {
        formValues.actual_date = dayjs(existingRiVisit.actual_date);
      }

      if (Object.keys(formValues).length > 0) {
        form.setFieldsValue(formValues);
      }
    }
  }, [open, followUps, form]);

  useEffect(() => {
    if (followUps.length > 0 && followUpStatuses.length > 0) {
      const sortedFollowUps = [...followUps].sort((a, b) => dayjs(b.created_at).diff(dayjs(a.created_at)));
      const latestFollowUp = sortedFollowUps[0];

      if (latestFollowUp && latestFollowUp.status_id) {
        const latestStatus = followUpStatuses.find(status => status.id === latestFollowUp.status_id);
        if (latestStatus && latestStatus.name.toLowerCase() === 'cold') {
          setIsRaDisabledByCold(true);
        } else {
          setIsRaDisabledByCold(false);
        }
      } else {
        setIsRaDisabledByCold(false);
      }
    } else {
      setIsRaDisabledByCold(false);
    }
  }, [followUps, followUpStatuses]);

  const fetchReasonInterest = async () => {
    setReasonInterestLoading(true);
    try {
      const response = await api.get(`leads/${leadId}/reason-interest`);
      if (response.data?.status === 'success') {
        const filteredReasons = response.data.data.filter((reason: any) => reason.reason !== 'Lain-lain');
        setReasonInterest(filteredReasons);
        const interestStatus = response.data.data[0]?.interest_status || 'tidak_minat';
        const label = `Alasan ${interestStatus.replace(/_/g, ' ')}`;
        setAlasanLabel(label.replace(/\b\w/g, (l: string) => l.toUpperCase()));
      }
    } catch (error) {
      console.error('Failed to fetch reason interest:', error);
      message.error('Gagal memuat alasan tidak minat.');
    } finally {
      setReasonInterestLoading(false);
    }
  };

  const handleStatusChange = (value: string) => {
    const selectedStatus = followUpStatuses.find(s => s.id === value);
    if (!selectedStatus) {
      setShowAlasanInput(false);
      return;
    }

    const statusName = selectedStatus.name.toLowerCase();

    const showConfirmation = (status: any) => {
      Modal.confirm({
        title: 'Peringatan',
        content: `Apakah Anda yakin memilih ${status.name.toUpperCase()}?`,
        okText: 'Yakin',
        cancelText: 'Batal',
        onOk() {
          if (statusName === 'cold') {
            setShowAlasanInput(true);
            fetchReasonInterest();
          } else {
            setShowAlasanInput(false);
          }
        },
        onCancel() {
          form.setFieldsValue({ status_id: undefined });
          setShowAlasanInput(false);
        },
      });
    };

    if (statusName === 'cold' || statusName === 'rsv') {
      showConfirmation(selectedStatus);
    } else {
      setShowAlasanInput(false);
    }
  };

  const handleReasonChange = (value: string) => {
    const selectedReason = reasonInterest.find(r => r.id === value);
    if (selectedReason && selectedReason.reason.toLowerCase() === 'lain-lain') {
      setShowOtherReasonInput(true);
    } else {
      setShowOtherReasonInput(false);
    }
  };

  const handleFinish = async (values: any) => {
    setLoading(true);

    const payload = {
      ...values,
      schedule_date: values.schedule_date
        ? dayjs(values.schedule_date).toISOString()
        : undefined,
      actual_date: values.actual_date
        ? dayjs(values.actual_date).toISOString()
        : undefined,
    };

    try {
      await api.post(`leads/${leadId}/follow-ups`, payload);
      message.success('Follow-up berhasil ditambahkan');
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      console.error('Failed to process follow-up:', error);
      const errorMessage = error.response?.data?.message || 'Gagal memproses follow-up';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Follow Up Activity"
      open={open}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
      >
        <Form.Item
          label="Action"
          name="action_id"
          rules={[{ required: true, message: 'Mohon pilih satu action' }]}
        >
          <Select placeholder="Select action">
            {followUpActions.map(action => (
              <Option key={action.id} value={action.id}>{action.name}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Status"
          name="status_id"
          rules={[{ required: true, message: 'Mohon pilih satu status' }]}
        >
          <Select placeholder="Select status" onChange={handleStatusChange}>
            {(() => {
              const latestFollowUp = followUps.sort((a, b) => dayjs(b.created_at).diff(dayjs(a.created_at)))[0];
              const latestStatus = followUpStatuses.find(s => s.id === latestFollowUp?.status_id);

              if (latestStatus?.name.toLowerCase() === 'cold') {
                const rsvOkStatus = followUpStatuses.find(s => s.name.toLowerCase() === 'rsv');
                if (rsvOkStatus) {
                  return <Option key={rsvOkStatus.id} value={rsvOkStatus.id}>{rsvOkStatus.name}</Option>;
                }
                return null;
              }

              return followUpStatuses.map(status => (
                <Option key={status.id} value={status.id}>{status.name}</Option>
              ));
            })()}
          </Select>
        </Form.Item>

        <Form.Item
          label="Kategori Minat"
          name="lead_follow_up_category_id"
          rules={[{ required: true, message: 'Mohon pilih satu kategori Minat' }]}
        >
          <Select placeholder="Select kategori Minat">
            {followUpCategories.map(category => (
              <Option key={category.id} value={category.id}>{category.name}</Option>
            ))}
          </Select>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Rencana Visit"
              name="schedule_date"
            >
              <DatePicker
                format="DD MMMM YYYY"
                style={{ width: '100%' }}
                placeholder="Pilih tanggal rencana visit"
                disabled={isRaVisitDone || isRaDisabledByCold}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Realisasi Visit"
              name="actual_date"
            >
              <DatePicker
                format="DD MMMM YYYY"
                style={{ width: '100%' }}
                placeholder="Pilih tanggal realisasi visit"
                disabled={isRiVisitDisabled}
              />
            </Form.Item>
          </Col>
        </Row>

        {showAlasanInput && (
          <Form.Item
            label={<span>{alasanLabel}</span>}
            name="reason_id"
            rules={[{ required: true, message: 'Mohon pilih alasan' }]}
          >
            <Select
              placeholder="Pilih alasan..."
              loading={reasonInterestLoading}
              onChange={handleReasonChange}
            >
              {reasonInterest.map(reason => (
                <Option key={reason.id} value={reason.id}>{reason.reason}</Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {showOtherReasonInput && (
          <Form.Item
            label="Alasan Lain"
            name="other_reason"
            rules={[{ required: true, message: 'Mohon masukkan alasan lain' }]}
          >
            <Input placeholder="Masukkan alasan lain" />
          </Form.Item>
        )}

        <Form.Item
          label="Respon"
          name="notes"
          rules={[{ required: true, message: 'Mohon tuliskan respon' }]}
        >
          <TextArea rows={4} placeholder="Tuliskan respon disini..." />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            block
            style={{ backgroundColor: '#7BAA18', borderColor: '#7BAA18' }}
          >
            Simpan Follow Up
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default FollowUpModal;
